import { IServiceUserInfo } from "../../datasolvr/ServicesFacade";
import { IUserContent } from "../../planEatApi/PlanEatFacade";
import { SysadminData } from "./SysadminData";

export const GROUPS = {
    unverified: "unverified_users",
    eater: "eater_users",
    eaterGroupAdmin: "eater_group_admin_users",
    kitchenManager: "kitchen_manager_users",
    officeManager: "office_manager_users",
    companyManager: "company_manager_users",
    systemAdmin: "system_admin_users",
};

export const groupLabels: Record<string, string> = {
    [GROUPS.unverified]: "Unverified",
    [GROUPS.eater]: "Eater",
    [GROUPS.eaterGroupAdmin]: "Eater group admin",
    [GROUPS.kitchenManager]: "Kitchen manager",
    [GROUPS.officeManager]: "Office manager",
    [GROUPS.companyManager]: "Company manager",
    [GROUPS.systemAdmin]: "System admin",
};

export const groupLabel = (group: string) => groupLabels[group] ?? group;

/**
 * Splits groups that were stored as one comma separated string (a backend bug when eater group admins are updated)
 * and removes duplicates.
 */
export function normalizeGroups(groups: string[] | undefined | null): string[] {
    const result: string[] = [];
    (groups ?? []).forEach(group => group.split(",").map(g => g.trim()).filter(Boolean).forEach(g => {
        if (!result.includes(g)) result.push(g);
    }));
    return result;
}

export type AccountStatus = "ok" | "unverified" | "unplaced" | "missingAccount";

export const statusLabels: Record<AccountStatus, string> = {
    ok: "OK",
    unverified: "Unverified",
    unplaced: "No eater group",
    missingAccount: "Missing account",
};

export interface AdminFor {
    companies: string[];
    offices: string[];
    eaterGroups: string[];
}

export interface Account {
    email: string;
    name: string;
    serviceUser?: IServiceUserInfo;
    /** Latest PlanEat user connected to the account (placement). */
    user?: IUserContent;
    groups: string[];
    groupsNeedRepair: boolean;
    adminFor: AdminFor;
    companyId?: string;
    status: AccountStatus;
}

export function adminForOf(data: SysadminData, email: string): AdminFor {
    return {
        companies: data.companies.filter(c => (c.content.companyAdminIds ?? []).includes(email)).map(c => c.content.id),
        offices: data.offices.filter(o => (o.content.officeAdminIds ?? []).includes(email)).map(o => o.content.id),
        eaterGroups: data.eaterGroups.filter(g => (g.content.eaterGroupAdminIds ?? []).includes(email)).map(g => g.content.id),
    };
}

/** One entry per login account plus PlanEat users whose login account no longer exists. */
export function buildAccounts(data: SysadminData): Account[] {
    const userByEmail = new Map(data.users.map(u => [u.content.applicationUserId, u.content]));
    const emails = new Set<string>([...data.serviceUsers.map(s => s.content.id), ...data.users.map(u => u.content.applicationUserId)]);
    const serviceUserByEmail = new Map(data.serviceUsers.map(s => [s.content.id, s]));

    return Array.from(emails).filter(Boolean).map(email => {
        const serviceUser = serviceUserByEmail.get(email);
        const user = userByEmail.get(email);
        const rawGroups = serviceUser?.content.data_raw?.groups ?? [];
        const groups = normalizeGroups(rawGroups);
        const companyId = data.companyIdOfEaterGroup(user?.eaterGroupId) ?? data.companyIdOfOffice(user?.officeId);
        // Staff (admins, kitchen managers, sysadmins) do not need an eater group
        const needsEaterGroup = groups.includes(GROUPS.eater) || groups.every(g => g === GROUPS.unverified);
        const status: AccountStatus = !serviceUser ? "missingAccount"
            : groups.includes(GROUPS.unverified) ? "unverified"
                : !user?.eaterGroupId && needsEaterGroup ? "unplaced" : "ok";
        return {
            email,
            name: serviceUser?.content.name ?? "",
            serviceUser,
            user,
            groups,
            groupsNeedRepair: JSON.stringify(groups) !== JSON.stringify(rawGroups),
            adminFor: adminForOf(data, email),
            companyId,
            status,
        };
    });
}

export const isAdmin = (account: Account) =>
    account.adminFor.companies.length + account.adminFor.offices.length + account.adminFor.eaterGroups.length > 0;

/** Companies the account belongs to: through its placement or as admin of the company, one of its offices or eater groups. */
export function companiesOf(data: SysadminData, account: Account): Set<string> {
    const ids = new Set<string>();
    const add = (id?: string) => id && ids.add(id);
    add(account.companyId);
    account.adminFor.companies.forEach(add);
    account.adminFor.offices.forEach(id => add(data.companyIdOfOffice(id)));
    account.adminFor.eaterGroups.forEach(id => add(data.companyIdOfEaterGroup(id)));
    return ids;
}

/**
 * Accounts visible for the selected company. Accounts that are not connected to any company are always included,
 * so new sign-ups can be found and placed.
 */
export function accountsForSelectedCompany(data: SysadminData, accounts: Account[]): Account[] {
    if (!data.selectedCompanyId) return accounts;
    return accounts.filter(account => {
        const companies = companiesOf(data, account);
        return companies.size === 0 || companies.has(data.selectedCompanyId!);
    });
}
